import { RevampTable, TableCell } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { checkCellValueStyle, NumericalCellValueStyle } from '@helpers';
import { HeaderRow } from '@terminal/components/price-movement/components/price-movement-table/components/header-row/HeaderRow';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { PercentageCell } from '../percentage-cell/PercentageCell';
import styles from './PredictionSummaryTable.module.scss';

const backgroundColors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish-soft)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish-soft)',
  [NumericalCellValueStyle.Neutral]: 'transparent',
};

const textColors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish)',
  [NumericalCellValueStyle.Neutral]: 'var(--text-default)',
};

export type PredictionSummaryTooltipParams = {
  rowId: string;
  horizon: string;
  value?: number;
};

export interface PredictionSummaryTableProps {
  data: Record<string, PredictionRow>;
  isMedian?: boolean;
  horizons: string[];
  isFixedIncome: boolean;
  tooltipText?: string;
  getTooltipText?: (
    params: PredictionSummaryTooltipParams
  ) => string | undefined;
}

export type PredictionRow = Record<string, number>;

export const PredictionSummaryTable = ({
  data,
  isMedian,
  horizons,
  isFixedIncome,
  tooltipText,
  getTooltipText,
}: PredictionSummaryTableProps) => {
  const { t } = useTranslation('scenario');

  const headerLabels = horizons.map(horizon => {
    /* i18next-extract-mark-context-next-line  ["1d", "2d", "1w", "2w", "1m", "3m", "6m", "1y"] */
    return t('scenario:drawer.summaryTable.horizon', {
      context: horizon,
    });
  });

  const rowsIds = ['max', 'p80', isMedian ? 'median' : 'mean', 'p20', 'min'];

  const renderTableBody = () =>
    rowsIds.map(rowId => {
      const horizonCells = horizons.map(horizon => {
        const mappedPrediction = data[horizon];
        const value = mappedPrediction?.[rowId];
        const percentageCell = (
          <PercentageCell suffix={isFixedIncome ? 'bps' : '%'} value={value} />
        );
        const cellTooltipText =
          getTooltipText?.({ rowId, horizon, value }) ?? tooltipText;

        return (
          <TableCell
            key={`body-${rowId}-${horizon}`}
            alignment="center"
            className={styles.numericalTableCell}
            data-numerical-cell
            style={
              {
                '--cell-bg': backgroundColors[checkCellValueStyle(value)],
                '--cell-text-color': textColors[checkCellValueStyle(value)],
              } as CSSProperties
            }
          >
            {cellTooltipText ? (
              <Tooltip label={cellTooltipText} trigger={TooltipTrigger.Hover}>
                <div className={styles.hoverableValue}>{percentageCell}</div>
              </Tooltip>
            ) : (
              percentageCell
            )}
          </TableCell>
        );
      });

      horizonCells.unshift(
        <TableCell key={`body-${rowId}`} alignment="start" isNumber={false}>
          {
            <TooltipContainer
              showUnderline
              label={t(`scenario:drawer.summaryTable.tooltip`, {
                context: rowId,
              })}
            >
              {
                <span>
                  {
                    /* i18next-extract-mark-context-next-line  ["max", "p80", "median", "mean", "p20", "min"] */

                    t(`scenario:drawer.summaryTable.row`, {
                      context: rowId,
                    })
                  }
                </span>
              }
            </TooltipContainer>
          }
        </TableCell>
      );
      return (
        <tr key={rowId} data-testid={rowId}>
          {horizonCells}
        </tr>
      );
    });

  return (
    <div
      className={styles.predictionSummaryTableRoot}
      data-testid="prediction-summary-table"
      data-clipboard-snapshot
    >
      <RevampTable className={styles.tableStyled} withBodyRowHover={false}>
        <thead>
          <HeaderRow labels={['', ...headerLabels]} />
        </thead>
        <tbody>{renderTableBody()}</tbody>
      </RevampTable>
    </div>
  );
};
